import React, { useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Play } from 'lucide-react';
import VideoLightbox from './VideoLightbox';

const VideoCard = ({ item, onClick }) => {
  const title = item.category ? item.category.name : item.subcategory ? item.subcategory.name : item.product?.name;
  const subtitle = (item.category || item.subcategory) ? null : `৳${Number(item.product?.price || 0).toLocaleString()}`;

  return (
    <button
      onClick={onClick}
      className="group relative shrink-0 w-32 sm:w-40 md:w-48 rounded-2xl overflow-hidden bg-gray-900 shadow-sm hover:shadow-xl transition-shadow duration-300 snap-start text-left"
      style={{ aspectRatio: '9/16' }}
    >
      {item.video_id ? (
        <img
          src={`https://img.youtube.com/vi/${item.video_id}/hqdefault.jpg`}
          alt={title}
          className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
          loading="lazy"
          decoding="async"
        />
      ) : (
        <div className="w-full h-full bg-gradient-to-br from-gray-800 to-gray-900" />
      )}

      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-black/20" />

      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center group-hover:bg-white/30 group-hover:scale-110 transition-all">
          <Play className="w-5 h-5 sm:w-6 sm:h-6 text-white fill-white ml-0.5" />
        </div>
      </div>

      <div className="absolute inset-x-0 bottom-0 p-2.5 sm:p-3">
        <p className="text-white text-xs sm:text-sm font-medium truncate drop-shadow-sm">{title}</p>
        {subtitle && <p className="text-white/75 text-[11px] sm:text-xs mt-0.5">{subtitle}</p>}
      </div>
    </button>
  );
};

const ProductVideos = ({ items }) => {
  const scrollerRef = useRef(null);
  const [activeItem, setActiveItem] = useState(null);

  if (!items || items.length === 0) return null;

  const scrollBy = (amount) => {
    scrollerRef.current?.scrollBy({ left: amount, behavior: 'smooth' });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-14">
      <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-5 sm:mb-7">Watch &amp; Shop</h2>

      <div className="relative">
        {items.length > 1 && (
          <>
            <button
              onClick={() => scrollBy(-300)}
              className="hidden md:flex absolute -left-4 top-1/2 -translate-y-1/2 z-10 items-center justify-center w-10 h-10 rounded-full bg-white shadow-lg border border-gray-100 text-gray-600 hover:text-primary-600 transition-colors"
              aria-label="Scroll videos left"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => scrollBy(300)}
              className="hidden md:flex absolute -right-4 top-1/2 -translate-y-1/2 z-10 items-center justify-center w-10 h-10 rounded-full bg-white shadow-lg border border-gray-100 text-gray-600 hover:text-primary-600 transition-colors"
              aria-label="Scroll videos right"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </>
        )}

        <div
          ref={scrollerRef}
          className="flex gap-3 sm:gap-4 overflow-x-auto pb-2 snap-x snap-mandatory scroll-smooth [&::-webkit-scrollbar]:hidden"
          style={{ scrollbarWidth: 'none' }}
        >
          {items.map(item => (
            <VideoCard key={item.id} item={item} onClick={() => setActiveItem(item)} />
          ))}
        </div>
      </div>

      {activeItem && (
        <VideoLightbox item={activeItem} onClose={() => setActiveItem(null)} />
      )}
    </div>
  );
};

export default ProductVideos;
